/* G5 Q4(a): PLP 商品カードのお気に入りハートを WishlistKing（Swish）実連携でトグル機能化。
 *
 * 案E（DECISIONS 2026-06-29・assets/tol-pdp-form.js の initWishlist）を PLP カード向けに移植した独立 JS。
 * PDP の initWishlist は「単一 scope・単一 button・.product-variant-id hidden input 依存・[data-tol-pdp] 起動」
 * ＝PLP では流用できないため、tol-pdp-form.js は無改修のまま別ファイルとして起こす（PDP 案E の非退行）。
 *
 * PLP 版の差分:
 *  - 複数 button（[data-tol-wishlist-card] を querySelectorAll）。
 *  - variantId は静的 data 属性（data-variant-id）＝variant セレクタが無い（variantChange 購読は不要）。
 *    同一性は variantId 単位（PDP 案E と一貫・selected_or_first_available_variant は公式上 nil にならない）。
 *  - loadWishlist はページで 1 回だけ呼び、全 button を一括 reflect（N カードでも 1 read）。click 時は
 *    その都度 read してトグル（案E と同じ { ok, item } で read 失敗と未登録を区別）。
 *  - state:refetch 購読は 1 本。発火時に 1 read → 全 button 再 reflect（busy 中の button だけ個別スキップ）。
 *  - LAZY ポーリングは共有 1 本（N timer にしない）。未ロード中は全 button hidden。
 *
 * WishlistKing API（DEV プローブ確定・案E ADR）:
 *  - addWishlistItem({ productHandle, variantId }) / removeWishlistItem({ wishlistItemId })（公開）
 *  - 状態取得は _apis.wishlist.loadWishlist({ wishlistId: 'mine' }) → items[].{ id(=wishlistItemId), variantId(number) }
 *    （公開の取得 API が無いため _apis 依存＝失敗時は操作中止に倒れる fail-safe・案E 受容残リスク③と同根）
 *  - events.subscribe('state:refetch', cb) で外部変更購読。appLoading:LAZY（未ロード時 window.WishlistKing undefined）。
 *  - ★書き込みロック（ds04-#2・2026-10-01 実測）: add/remove は内部ロック中なら undefined を返して何もしない。
 *    解除には WK.state.observeWishlist の購読者が要る＝下の ensureObserver。戻り値が空なら「効かなかった」と扱う。
 *
 * 規律（tol-pdp-form.js / tol-ugc-modal.js 前例）: custom element 作らない・アロー IIFE +
 *  window.__tolWishlistCardLoaded ガード + data-*-init ガード + AbortController teardown +
 *  shopify:section:load 対応。
 *
 * ★R21 で前提が変わった（旧記述「フィルタの Ajax 差替経路は現状ゼロ＝再 hydrate は不要」は撤回）:
 *  smart-product-filter（globo）を商品グリッドに結線すると、globo がグリッドの innerHTML を
 *  自前テンプレートで全置換する（絞り込み未適用の初期表示でも通る）。カード内のハート <button> は
 *  総入れ替えになるが、**グリッド要素そのものは残る**ため initGrid の冪等ガード
 *  （grid.dataset.tolWishlistInit）に当たって再 init が黙って no-op する＝新しいボタンに
 *  リスナーが付かない。下部の globoFilterRenderCompleted 購読でフラグを戻して貼り直す。
 */
(() => {
  if (window.__tolWishlistCardLoaded) return;
  window.__tolWishlistCardLoaded = true;

  /* WishlistKing が必要な公開 API を備えているか（partial hydrate / バージョン差対策）。 */
  const isReady = (WK) => !!WK
    && typeof WK.addWishlistItem === 'function'
    && typeof WK.removeWishlistItem === 'function'
    && WK._apis && WK._apis.wishlist && typeof WK._apis.wishlist.loadWishlist === 'function';

  /* button の variantId（静的属性・number）。未解決なら null（理論上は起きない＝Liquid で非出力）。 */
  const variantIdOf = (btn) => {
    const n = btn.dataset.variantId ? parseInt(btn.dataset.variantId, 10) : NaN;
    return Number.isFinite(n) ? n : null;
  };

  const reflect = (btn, on) => {
    btn.setAttribute('aria-pressed', on ? 'true' : 'false');
    btn.classList.toggle('is-active', on);
    btn.setAttribute('aria-label', on ? btn.dataset.labelOn : btn.dataset.labelOff);
  };

  /* wishlist 全 item を 1 回だけ読む。戻り { ok, items }: ok=false は read 失敗（状態不明）。 */
  const loadItems = async (WK) => {
    try {
      const res = await WK._apis.wishlist.loadWishlist({ wishlistId: 'mine' });
      const items = (res && res.wishlist && res.wishlist.items) || [];
      return { ok: true, items };
    } catch (_) {
      return { ok: false, items: [] };
    }
  };

  /* items から variantId 一致の登録 item を返す（number 同士・混在突合しない）。 */
  const findItem = (items, variantId) => items.find((it) => it.variantId === variantId) || null;

  /* ds04-#2: Swish の書き込みロックを外す購読（ページで 1 本・tol-pdp-form.js と window で共有）。
   * WK.state の書き込み系（add/remove 等）は内部ロック中なら何もせず undefined を返す。ロックは書き込みで立ち、
   * 解除は state.observeWishlist() の購読ハンドラ（state:refetch → loadWishlist 完了時）だけ＝購読者が
   * いないと 1 回書き込んだページでは二度と外れない（DEV 実測 2026-10-01・公式ドキュメントでは未確認）。
   * Swish 純正のボタン等を置かない当テーマでは自前で 1 本張る。張るのは最初のクリック時（閲覧だけのページで
   * Swish への呼び出しを増やさない）。callback は waitRelease の待ち手を起こすだけ（表示は state:refetch 側）。
   * 同じ関数を tol-pdp-form.js にも置く（受容残⑥と同じ二重化）。 */
  const ensureObserver = (WK) => {
    const st = WK && WK.state;
    if (!st || typeof st.observeWishlist !== 'function') return null;
    const cur = window.__tolWishlistObserver;
    if (cur && cur.state === st) return cur;
    try {
      const hub = { state: st, waiters: [] };
      st.observeWishlist({ wishlistId: 'mine' }).subscribe(() => {
        const ws = hub.waiters.splice(0);
        setTimeout(() => ws.forEach((w) => w()), 0); /* ロック解除は同じ callback の直後＝1 tick 遅らせる */
      });
      window.__tolWishlistObserver = hub;
      return hub;
    } catch (_) {
      return null;
    }
  };

  /* 購読の次の通知（＝ロック解除）を最大 ms 待つ。true = 通知あり／false = 時間切れ。 */
  const waitRelease = (hub, ms) => new Promise((resolve) => {
    let timer = 0;
    const waiter = () => { clearTimeout(timer); resolve(true); };
    timer = setTimeout(() => {
      const i = hub.waiters.indexOf(waiter); /* 時間切れの待ち手は共有の配列から外す（溜めない） */
      if (i !== -1) hub.waiters.splice(i, 1);
      resolve(false);
    }, ms);
    hub.waiters.push(waiter);
  });

  /* 1 回分のトグル。戻り { result, was }: result = 書き込み後の登録状態（true/false）／UNKNOWN（read 失敗＝
   * 何もしない）／SKIPPED（Swish が書き込みを受け付けなかった＝ロック中など。成功時は { wishlistItem } が返る）。
   * was = 書き込み前に読んだ登録状態。
   * expectWas を渡したとき（やり直し）は、読み直した状態が 1 回目に読んだ状態と同じときだけ書き込む。違えば
   * （間に別の書き込みが入った・空振りに見えた書き込みが実は効いていた）書き込まずにその状態を返す
   * ＝クリックの意図と逆の書き込みをしない。 */
  const UNKNOWN = 'unknown';
  const SKIPPED = 'skipped';
  const toggleOnce = async (wk, handle, variantId, expectWas) => {
    const { ok, items } = await loadItems(wk);
    if (!ok) return { result: UNKNOWN, was: null }; /* fail-safe(3): 状態不明なら add/remove しない */
    const item = findItem(items, variantId);
    const was = !!item;
    if (expectWas !== undefined && was !== expectWas) return { result: was, was };
    const res = item
      ? await wk.removeWishlistItem({ wishlistItemId: item.id })
      : await wk.addWishlistItem({ productHandle: handle, variantId: variantId });
    if (!res) return { result: SKIPPED, was };
    return { result: !was, was, wrote: true };
  };

  /* 各 button の busy フラグは button 要素に持たせる（複数独立） */
  const isBusy = (btn) => btn.dataset.tolWishlistBusy === '1';
  const setBusy = (btn, v) => { btn.dataset.tolWishlistBusy = v ? '1' : '0'; };

  /* button ごとのクリックの世代（クリック開始で +1）。syncAll は読み始めた時点の世代のままの button にだけ反映する
   * ＝前の書き込みの state:refetch で始まった読みが、次のクリックの成功の後に遅れて返っても古い状態で上書きしない
   * （ds04-#2 の DEV 実測: 解除の直後に、1 つ前の追加の再読み込みが「登録あり」を映し戻した）。 */
  const gens = new WeakMap();
  const genOf = (btn) => gens.get(btn) || 0;
  /* busy 中で syncAll が飛ばした button。busy が外れたらその button を読み直す＝書き込みの後に busy を保っている間に
   * 別タブ等で登録が変わっても、表示が古いまま残らない（Codex 実 diff 4 回目 P2・tol-pdp-form.js の pendingSync と同じ）。 */
  const pendingBtns = new WeakSet();

  /* 全 button を 1 read で一括 reflect（busy 中・読みの最中にクリックされた button はスキップ）。 */
  const syncAll = async (WK, buttons) => {
    const before = buttons.map(genOf);
    const { ok, items } = await loadItems(WK);
    if (!ok) return; /* read 失敗時は表示を変えない（未登録に誤誘導しない） */
    buttons.forEach((btn, i) => {
      if (isBusy(btn)) { pendingBtns.add(btn); return; }
      if (genOf(btn) !== before[i]) return;
      const variantId = variantIdOf(btn);
      if (variantId == null) return;
      reflect(btn, !!findItem(items, variantId));
    });
  };

  const bindButton = (btn, WK, signal) => {
    const handle = btn.dataset.productHandle;
    if (!handle) return;
    btn.addEventListener('click', async () => {
      const wk = window.WishlistKing;
      if (!isReady(wk) || isBusy(btn)) return; /* fail-safe(1): 未ロード/未対応/busy は無反応 */
      const variantId = variantIdOf(btn);
      if (variantId == null) return; /* fail-safe(2): variant 未解決では操作しない */
      setBusy(btn, true);
      gens.set(btn, genOf(btn) + 1);
      /* 再 read して真の状態へ整合。読み始めた後に次のクリックが始まっていたら反映しない。read も失敗なら現表示維持。 */
      const resync = async () => {
        const wk2 = window.WishlistKing;
        if (!isReady(wk2)) return;
        const g = genOf(btn);
        const r = await loadItems(wk2);
        if (r.ok && g === genOf(btn)) reflect(btn, !!findItem(r.items, variantId));
      };
      let hub = null;
      let released = true; /* 自分の書き込みの後、Swish の再読み込み（キャッシュ更新）の完了を確認できたか */
      try {
        hub = ensureObserver(wk);
        let r = await toggleOnce(wk, handle, variantId);
        /* ロック中（前の書き込みの後始末が終わっていない）に当たったら、busy のまま解除を待って 1 回だけやり直す
           ＝回線の遅い端末で 1 秒間隔に押しても、押した 1 回を捨てない（ds04-#2）。通知が来ず時間切れでも 1 回は
           確かめる（読み直しが 1 回目と同じときだけ書く＝誤った書き込みはしない・まだロック中なら SKIPPED のまま）。 */
        if (r.result === SKIPPED && hub) {
          await waitRelease(hub, 3000);
          r = await toggleOnce(window.WishlistKing, handle, variantId, r.was);
        }
        if (r.result === SKIPPED) await resync(); /* 効かなかった＝表示を変えずに真の状態へ（嘘の表示をしない） */
        else if (r.result !== UNKNOWN) reflect(btn, r.result);
        /* 自分の書き込みが効いたら、Swish の後始末（再読み込み＝ロック解除とキャッシュ更新）が終わるまで busy を保つ。
           直後のクリックは loadWishlist が書き込み前のキャッシュを返す（DEV 実測: 完了 8ms 後の読みが旧状態）＝
           旧状態を前提に動いて利用者の操作が失われるのを防ぐ。表示は上で先に切り替える。 */
        if (r.wrote && hub) released = await waitRelease(hub, 3000);
      } catch (_) {
        await resync(); /* write 失敗: 楽観更新の巻き戻し */
      } finally {
        setBusy(btn, false);
        /* busy 中に syncAll が飛ばしていたら読み直す。再読み込みの完了を確認できないまま（時間切れ）読むと
           書き込み前のキャッシュを映しうるので、その場合は完了の通知まで遅らせる。遅らせた時点で次のクリックが
           進行中なら、そのクリックの終わりに任せる。 */
        if (pendingBtns.has(btn)) {
          pendingBtns.delete(btn);
          const later = () => {
            if (isBusy(btn)) { pendingBtns.add(btn); return; }
            resync();
          };
          if (released || !hub) later();
          else hub.waiters.push(later);
        }
      }
    }, { signal });
  };

  /* WishlistKing 到来後: 全 button を bind → hidden 解除 → 初期一括 reflect → state:refetch 購読。
   * ★abort 済みチェックが 2 箇所必要（R21 で顕在化）: `await syncAll()` の間に teardown が走ると、
   *   ①既に abort 済みの signal へ 'abort' リスナーを足しても**二度と発火しない**ので購読が解除されず
   *     leak する ②解除済みのボタンに hidden を外して見せてしまう。
   *   R21 で globo の再描画ごとに init し直す（`globoFilterRenderCompleted`）ようになり、旧
   *   `shopify:section:load` 経路よりはるかに高頻度で通るため、稀なエッジではなく通常経路になった。 */
  const wire = async (WK, buttons, signal) => {
    buttons.forEach((btn) => bindButton(btn, WK, signal));
    await syncAll(WK, buttons); /* フラッシュ回避: hidden のまま初期状態を確定 */
    if (signal.aborted) return;
    buttons.forEach((btn) => { btn.hidden = false; });
    try {
      if (WK.events && typeof WK.events.subscribe === 'function') {
        const off = WK.events.subscribe('state:refetch', () => { syncAll(window.WishlistKing, buttons); });
        if (typeof off === 'function') {
          if (signal.aborted) off();
          else signal.addEventListener('abort', off, { once: true });
        }
      }
    } catch (_) { /* 購読不可でも click 時 reflect で機能は保たれる */ }
  };

  const initGrid = (grid, signal) => {
    if (grid.dataset.tolWishlistInit === '1') return;
    grid.dataset.tolWishlistInit = '1';
    const buttons = Array.prototype.slice.call(grid.querySelectorAll('[data-tol-wishlist-card]'));
    if (!buttons.length) return;
    /* aria-label のトグル用（機能化前の初期 label は Liquid が「追加」を焼いている）。 */
    buttons.forEach((btn) => {
      btn.dataset.labelOff = btn.getAttribute('aria-label');
      btn.dataset.labelOn = btn.dataset.labelOnText || btn.dataset.labelOff;
    });
    if (isReady(window.WishlistKing)) {
      wire(window.WishlistKing, buttons, signal);
    } else {
      /* LAZY ロード待ち（共有 1 本のポーリング）: 未ロード中は全 button hidden（死んだボタンを出さない）。 */
      buttons.forEach((btn) => { btn.hidden = true; });
      let tries = 0;
      const timer = setInterval(() => {
        tries += 1;
        if (isReady(window.WishlistKing)) { clearInterval(timer); wire(window.WishlistKing, buttons, signal); }
        else if (tries >= 40) { clearInterval(timer); } /* 約10秒で諦め（hidden 維持＝App embed 無効と同義の fail-safe） */
      }, 250);
      signal.addEventListener('abort', () => clearInterval(timer), { once: true });
    }
  };

  /* ---- 起動 ---- */
  const controllers = new WeakMap();

  const boot = (root) => {
    const grids = (root || document).querySelectorAll('[data-tol-wishlist-grid]');
    grids.forEach((grid) => {
      const ctrl = new AbortController();
      controllers.set(grid, ctrl);
      initGrid(grid, ctrl.signal);
    });
  };

  boot(document);

  /* R21: globo が描いたグリッド（#gf-products）は data-tol-wishlist-grid を持たない面もある
   * （検索面 = Dawn の main-search 由来）。さらに defer の本スクリプトより先に globo の
   * globoFilterRenderCompleted が発火し終えている可能性があるため、起動時にも一度拾う。 */
  const bootGloboGrid = () => {
    const grid = document.getElementById('gf-products');
    if (!grid || !grid.querySelector('[data-tol-wishlist-card]')) return;
    if (grid.dataset.tolWishlistInit === '1') return;
    const prev = controllers.get(grid);
    if (prev) prev.abort();
    const ctrl = new AbortController();
    controllers.set(grid, ctrl);
    initGrid(grid, ctrl.signal);
  };
  bootGloboGrid();

  /* テーマエディターでのセクション再描画に対応（section:load で当該セクション内を再 init）。
   * 旧セクションの teardown（controller abort）は section:load 前に発火する section:unload が担う
   * （WeakMap キーは旧 grid 要素・再レンダ後の新 grid は controllers に未登録なので下の abort は基本 no-op。
   * unload が発火しない経路への保険として残す）。init フラグを '0' に戻して boot で新 controller を貼る。 */
  /* R21: globo（smart-product-filter）がグリッドを再描画したあとの再バインド。
   * globo は結線したグリッドの id を gf-products へ書き換えるので、対象は #gf-products に限定する
   * ＝同一ページに同居する tol-collection-ranking / tol-collection-recent（同じ
   * data-tol-wishlist-grid を持つ）を二重バインドしない。
   * 手順は shopify:section:load と同じ（旧 controller を abort → init フラグを戻す → 貼り直す）。 */
  window.addEventListener('globoFilterRenderCompleted', () => {
    const grid = document.getElementById('gf-products');
    /* 判定は「ハート <button> が実際に在るか」で行う。data-tol-wishlist-grid では判定できない
       ——検索面のグリッドは Dawn の main-search 由来でこの属性を持たないが、globo が
       テーマ供給テンプレート（#gspfProduct）でハート付きカードを描くため、属性で弾くと
       検索面のハートがリスナー無しの「死んだボタン」になる。 */
    if (!grid || !grid.querySelector('[data-tol-wishlist-card]')) return;
    const prev = controllers.get(grid);
    if (prev) prev.abort();
    grid.dataset.tolWishlistInit = '0';
    const ctrl = new AbortController();
    controllers.set(grid, ctrl);
    initGrid(grid, ctrl.signal);
  });

  document.addEventListener('shopify:section:load', (e) => {
    const scope = e.target;
    scope.querySelectorAll('[data-tol-wishlist-grid]').forEach((grid) => {
      const prev = controllers.get(grid); /* 通常 undefined（新要素）＝保険 */
      if (prev) prev.abort();
      grid.dataset.tolWishlistInit = '0';
    });
    boot(scope);
  });

  document.addEventListener('shopify:section:unload', (e) => {
    e.target.querySelectorAll('[data-tol-wishlist-grid]').forEach((grid) => {
      const prev = controllers.get(grid);
      if (prev) prev.abort();
    });
  });
})();
